import React, { useState, useCallback } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Slider,
    IconButton,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Cropper from 'react-easy-crop';

const AlbumCoverSelector = ({
                                open,
                                onClose,
                                imageSrc,
                                onCoverSelected,
                                aspect = 16 / 9,
                            }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedArea, setCroppedArea] = useState(null);

    const handleClose = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedArea(null);
        onClose();
    };

    const onCropComplete = useCallback((croppedArea) => {
        setCroppedArea(croppedArea);
    }, []);

    const handleSave = () => {
        if (!croppedArea) return;
        onCoverSelected(btoa(JSON.stringify({ x: croppedArea.x, y: croppedArea.y, w: croppedArea.width, h: croppedArea.height })));
        handleClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={isMobile}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        height: isMobile ? '100%' : '90vh',
                        m: isMobile ? 0 : undefined,
                    },
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Select Album Cover
                    </Typography>
                    <IconButton onClick={handleClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        p: 0,
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: '#fafafa',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            minHeight: isMobile ? 'calc(100vh - 180px)' : 500,
                        }}
                    >
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                            objectFit="contain"
                            style={{
                                containerStyle: {
                                    width: '100%',
                                    height: '100%',
                                    position: 'absolute',
                                },
                            }}
                        />
                    </Box>

                    {/* Zoom Controls */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: isMobile ? 80 : 24,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: 'rgba(0,0,0,0.75)',
                            color: 'white',
                            px: 3,
                            py: 1.5,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            zIndex: 20,
                            minWidth: isMobile ? 280 : 320,
                        }}
                    >
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            Zoom
                        </Typography>
                        <Slider
                            value={zoom}
                            min={1}
                            max={5}
                            step={0.05}
                            onChange={(_, value) => setZoom(value)}
                            sx={{ width: isMobile ? 180 : 220, color: '#fff' }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 40 }}>
                            {zoom.toFixed(1)}×
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} color="inherit" fullWidth={isMobile}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!croppedArea}
                        fullWidth={isMobile}
                    >
                        Use as Album Cover
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AlbumCoverSelector;